<template>
  <div ref="mapContainer" class="allStoresMap"></div>
</template>

<script>
import { onMounted, onBeforeUnmount, ref, watch } from "vue";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import positionIcon from "@/assets/position.png";
import positionOtherIcon from "@/assets/position_other.png";
import { MAP_REGION_CN, MAP_REGION_MY, getMapRegionDefaultLocation } from "@/utils/config";
import { hasValidMapCoordinates } from "@/views/home/maptiler/useMapTilerAllStores";
import {
  applyHomeMapStyleEnhancements,
  CHINESE_IDEOGRAPH_FONT,
  getMapTilerStoresStyleUrl,
  MAP_LABEL_LANGUAGE,
  shouldUseChineseIdeographFont,
} from "@/views/home/maptiler/mapTilerLanguageUtils";
import {
  clearMapCameraPadding,
  getRegionOverviewMaxZoom,
  getRegionOverviewFallbackZoom,
  mergeRegionOverviewBounds,
  getPcMapFitPadding,
  PC_MY_OVERVIEW_CENTER_LNG_OFFSET,
  easeMapToSelectedDealer,
  afterOverviewFitBounds,
} from "@/views/home/maptiler/mapOverviewUtils";
import {
  createDealerMarkerElement,
  getDealerCoordKey,
  readDealerLatLng,
} from "@/views/home/maptiler/mapDealerMarkerUtils";
import { createMapRegionRefreshScheduler } from "@/views/home/maptiler/mapRegionRefreshScheduler";

export default {
  name: "MapTilerAllStoresMap",
  props: {
    dealers: { type: Array, default: () => [] },
    mapRegion: { type: String, default: MAP_REGION_CN },
    activeKey: { type: [String, Number], default: null },
    pcOverview: { type: Boolean, default: false },
    mapLabelLanguage: {
      type: String,
      default: MAP_LABEL_LANGUAGE.ZH,
    },
  },
  emits: ["marker-click", "map-ready"],
  setup(props, { emit, expose }) {
    const mapContainer = ref(null);
    const map = ref(null);
    const dealerMarkers = [];
    const locationMarkers = new Map();
    let activeMarkerEntry = null;
    let lastStyleRegion = null;
    let lastMapLabelLanguage = null;

    const applyMapStyleIfNeeded = () => {
      if (!map.value) return;
      const regionChanged = props.mapRegion !== lastStyleRegion;
      const langChanged = props.mapLabelLanguage !== lastMapLabelLanguage;
      if (!regionChanged && !langChanged) return;
      applyHomeMapStyleEnhancements(map.value, {
        mapRegion: props.mapRegion,
        mapLabelLanguage: props.mapLabelLanguage,
      });
      lastStyleRegion = props.mapRegion;
      lastMapLabelLanguage = props.mapLabelLanguage;
    };

    const removeMarker = (marker) => {
      if (marker) {
        try {
          marker.remove();
        } catch (e) {}
      }
    };

    const clearDealerMarkers = () => {
      dealerMarkers.forEach(removeMarker);
      dealerMarkers.splice(0, dealerMarkers.length);
      locationMarkers.clear();
      activeMarkerEntry = null;
    };

    const getMapFitPadding = () =>
      props.pcOverview
        ? getPcMapFitPadding(props.mapRegion)
        : { top: 90, bottom: 60, left: 480, right: 60 };

    const getRegionSwitchZoom = () =>
      props.mapRegion === MAP_REGION_CN
        ? getRegionOverviewFallbackZoom(MAP_REGION_CN, props.pcOverview)
        : 5;

    const getAllDealerBounds = () => {
      const bounds = new maplibregl.LngLatBounds();
      let hasBounds = false;
      (props.dealers || []).forEach((element) => {
        if (!hasValidMapCoordinates(element)) return;
        // todo  
      //  if(element.fullName=='嘉实多汽车车膜旗舰店（天猫）'){
      //     element.latitude = "31.2104";
      //     element.longitude = "121.41819";
      //   }

        bounds.extend([
          Number(element.longitude ?? element.lng),
          Number(element.latitude ?? element.lat),
        ]);
        hasBounds = true;
      });
      return { bounds, hasBounds };
    };

    const getOverviewBounds = () => {
      const { bounds, hasBounds } = getAllDealerBounds();
      if (mergeRegionOverviewBounds(bounds, props.mapRegion)) {
        return { bounds, hasBounds: true };
      }
      return { bounds, hasBounds };
    };

    const fitAllDealersBounds = (animated = false) => {
      if (!map.value) return;
      clearMapCameraPadding(map.value);
      clearActiveDealerHighlight();
      const { bounds, hasBounds } = getOverviewBounds();
      const duration = animated ? 400 : 0;
      if (hasBounds) {
        map.value.fitBounds(bounds, {
          padding: getMapFitPadding(),
          maxZoom: getRegionOverviewMaxZoom(props.mapRegion, props.pcOverview),
          duration,
        });
        afterOverviewFitBounds(map.value, props.mapRegion, {
          animated,
          duration,
          pc: props.pcOverview,
        });
        return;
      }
      const fallback = getMapRegionDefaultLocation(props.mapRegion);
      const centerLng =
        fallback.lng +
        (props.pcOverview && props.mapRegion === MAP_REGION_MY
          ? PC_MY_OVERVIEW_CENTER_LNG_OFFSET
          : 0);
      map.value.easeTo({
        center: [centerLng, fallback.lat],
        zoom: getRegionOverviewFallbackZoom(props.mapRegion, props.pcOverview),
        duration,
      });
    };

    const clearActiveDealerHighlight = () => {
      if (activeMarkerEntry) {
        activeMarkerEntry.setActive(false);
        activeMarkerEntry = null;
      }
    };

    const setLocationActive = (lngNum, latNum, active) => {
      const key = getDealerCoordKey(lngNum, latNum);
      const entry = locationMarkers.get(key);
      if (!entry) return;

      if (active) {
        if (activeMarkerEntry && activeMarkerEntry !== entry) {
          activeMarkerEntry.setActive(false);
        }
        entry.setActive(true);
        activeMarkerEntry = entry;
        return;
      }

      entry.setActive(false);
      if (activeMarkerEntry === entry) {
        activeMarkerEntry = null;
      }
    };

    const resetOverview = () => {
      fitAllDealersBounds(true);
    };

    const emitMarkerClick = (pos) => {
      const latNum = Number(pos.lat ?? pos.endLatitude);
      const lngNum = Number(pos.lng ?? pos.endLongitude);
      if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
        setLocationActive(lngNum, latNum, true);
      }
      emit("marker-click", {
        id: "dealer",
        fullName: pos.fullName,
        address: pos.address,
        endLatitude: pos.endLatitude,
        endLongitude: pos.endLongitude,
        groupDealers: pos.groupDealers || [],
      });
    };

    const renderDealerMarkers = () => {
      if (!map.value) return;
      clearDealerMarkers();

      const grouped = new Map();
      (props.dealers || []).forEach((element) => {
        if (!hasValidMapCoordinates(element)) return;
        const coords = readDealerLatLng(element);
        if (!coords) return;
        const { lat: latNum, lng: lngNum } = coords;
        const key = getDealerCoordKey(lngNum, latNum);
        if (!grouped.has(key)) {
          grouped.set(key, { latNum, lngNum, dealers: [] });
        }
        grouped.get(key).dealers.push(element);
      });

      grouped.forEach((group) => {
        const first = group.dealers[0];
        const count = group.dealers.length;
        const pos = {
          lat: group.latNum,
          lng: group.lngNum,
          fullName: first?.fullName,
          address: first?.fullAddress || first?.address,
          endLatitude: group.latNum,
          endLongitude: group.lngNum,
          groupDealers: group.dealers,
        };

        const coordKey = getDealerCoordKey(group.lngNum, group.latNum);
        const { root, setActive } = createDealerMarkerElement({
          defaultIconUrl: positionOtherIcon,
          activeIconUrl: positionIcon,
          count,
          onClick: () => emitMarkerClick(pos),
        });
        const marker = new maplibregl.Marker({ element: root, anchor: "bottom" })
          .setLngLat([group.lngNum, group.latNum])
          .addTo(map.value);
        dealerMarkers.push(marker);
        locationMarkers.set(coordKey, { marker, setActive });
      });

      if ((props.dealers || []).length > 0) {
        fitAllDealersBounds(false);
      }
    };

    const focusDealer = ({ longitude, latitude }) => {
      if (
        !map.value ||
        !hasValidMapCoordinates({
          latitude,
          longitude,
        })
      ) {
        return;
      }
      const lngNum = Number(longitude);
      const latNum = Number(latitude);
      easeMapToSelectedDealer(map.value, {
        lng: lngNum,
        lat: latNum,
        mapRegion: props.mapRegion,
        padding: getMapFitPadding(),
      });
      setLocationActive(lngNum, latNum, true);
    };

    const destroyMap = () => {
      clearDealerMarkers();
      if (map.value) {
        try {
          map.value.remove();
        } catch (e) {}
        map.value = null;
      }
    };

    const initMap = () => {
      if (!mapContainer.value) return;
      destroyMap();
      const fallback = getMapRegionDefaultLocation(props.mapRegion);
      map.value = new maplibregl.Map({
        container: mapContainer.value,
        style: getMapTilerStoresStyleUrl(
          props.mapRegion,
          props.mapLabelLanguage
        ),
        center: [fallback.lng, fallback.lat],
        zoom: getRegionSwitchZoom(),
        fadeDuration: 0,
        refreshExpiredTiles: false,
        ...(shouldUseChineseIdeographFont(props.mapLabelLanguage)
          ? { localIdeographFontFamily: CHINESE_IDEOGRAPH_FONT }
          : {}),
      });
      map.value.on("load", () => {
        applyHomeMapStyleEnhancements(map.value, {
          mapRegion: props.mapRegion,
          mapLabelLanguage: props.mapLabelLanguage,
        });
        lastStyleRegion = props.mapRegion;
        lastMapLabelLanguage = props.mapLabelLanguage;
        renderDealerMarkers();
        emit("map-ready", true);
      });
    };

    const refreshMapForRegion = () => {
      if (!map.value) return;
      const run = () => {
        applyMapStyleIfNeeded();
        if (!(props.dealers || []).length) {
          clearDealerMarkers();
          fitAllDealersBounds(false);
          return;
        }
        renderDealerMarkers();
      };
      if (map.value.loaded()) {
        run();
      } else {
        map.value.once("load", run);
      }
    };

    const scheduleRegionRefresh = createMapRegionRefreshScheduler(
      refreshMapForRegion
    );

    watch(() => props.mapRegion, scheduleRegionRefresh);
    watch(() => props.dealers, scheduleRegionRefresh, { deep: true });
    watch(() => props.mapLabelLanguage, scheduleRegionRefresh);

    onMounted(() => initMap());
    onBeforeUnmount(() => destroyMap());

    expose({ focusDealer, resetOverview });

    return { mapContainer };
  },
};
</script>

<style lang="scss" scoped>
.allStoresMap {
  width: 100%;
  height: 100%;
}
</style>
